import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import {
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  map,
  merge,
  of,
  switchMap,
} from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ReservationService } from '../../../../../services/reservation.service';
import {
  SMS_CONSENT,
  MAX_STAFF_PARTY_SIZE,
  AvailabilitySlot,
  CreateReservationInput,
  ExistingReservation,
  GuestLookup,
  RESERVATION_STATUS_LABELS,
  Reservation,
  ReservationSettings,
  ServicePeriod,
  ServingHoursState,
} from '../../../../../models/reservation';
import {
  atTime,
  formatInZone,
  periodEnd,
  timeLabel,
  toServiceDate,
} from '../../../../../services/reservation-time.util';
import { US_PHONE_PATTERN, formatUsPhone, usPhoneDigits } from '../../../../../utils/us-phone.util';
import { guestNameValidator } from '../../../../../utils/guest-name.util';
import { PickerOnlyDateDirective } from '../../../../../directives/picker-only-date.directive';
import { dateLabel } from '../../../../../utils/serving-hours.util';
import {
  returningGuestSummary,
  standingNotesChange,
} from '../../../../../utils/guest-history.util';

/**
 * A booking or request needs a way to reach the guest: a phone or an email,
 * or both. `{ contactRequired: true }` on the form when both are empty.
 * Walk-ins are different; they can be called by name at the host stand.
 */
export const contactRequired: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const phone = String(group.get('phone')?.value ?? '').trim();
  const email = String(group.get('email')?.value ?? '').trim();
  return phone || email ? null : { contactRequired: true };
};

/** The email rule Add staff uses. */
const EMAIL_PATTERN = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/;

export interface AddReservationDialogData {
  reservation?: Reservation;
  date?: string;
  settings?: ReservationSettings | null;
  /** The restaurant's IANA zone; slots and "today" are on its clock. */
  timeZone?: string;
}

export type SlotsEmptyReason = 'unset' | 'closed' | 'closed_date' | 'full' | 'error';

/**
 * New / edit reservation dialog. Party size and date drive an availability
 * lookup; the host picks a slot from what the engine says is bookable, so a
 * confirmation made here is one the floor can honour.
 */
import { TableNamesPipe, tableNamesLabel } from '../../../../../pipes/table-names.pipe';
@Component({
  selector: 'app-add-reservation-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTooltipModule,
    PickerOnlyDateDirective,
    TableNamesPipe,
  ],
  templateUrl: './add-reservation-modal.component.html',
  styleUrl: './add-reservation-modal.component.css',
})
export class AddReservationModalComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<AddReservationModalComponent>);
  readonly data = inject<AddReservationDialogData>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly reservationService = inject(ReservationService);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);

  /**
   * The guest typed into New reservation, found by phone or email before
   * saving (FNF-442): null for a first-timer, or until the lookup answers.
   */
  returning: GuestLookup | null = null;
  /** Allergies / preferences were filled in from `returning`, not typed. */
  notesFromHistory = false;
  /**
   * The lookup answered and nobody here has that phone or email: a first
   * visit. False while there is nothing to look up, or no lookup to ask.
   */
  firstTime = false;
  /** Toasts already shown for the guest being typed, so each shows once. */
  private greeted = { existing: false, returning: false };

  readonly isEdit = !!this.data?.reservation;
  periods: ServicePeriod[] = [];
  readonly occasions = ['Birthday', 'Anniversary', 'Business', 'Date night', 'Celebration'];

  form!: FormGroup;
  slots: AvailabilitySlot[] = [];
  loadingSlots = false;
  slotsError = false;
  hoursState: ServingHoursState = 'open';
  /** The restaurant's note for a closed date, e.g. "Christmas". */
  dayNote: string | null = null;
  selectedStartAt: string | null = null;
  selectedSlot: AvailabilitySlot | null = null;
  timeZone = this.data?.timeZone;
  today = toServiceDate(new Date(), this.timeZone);
  /** A year ahead: the calendar's last day. */
  maxDate = toServiceDate(new Date(Date.now() + 365 * 86_400_000), this.timeZone);

  private lookup = new Subject<{ date: string; party: number; attempt: number }>();
  private attempt = 0;

  /** Largest party staff can book here; online guests stop at maxParty. */
  readonly maxStaffParty = MAX_STAFF_PARTY_SIZE;
  /** SMS opt-in under the phone field (10DLC registration wording). */
  readonly smsConsent = SMS_CONSENT;

  get maxParty(): number {
    return this.data?.settings?.party.max ?? 20;
  }

  get autoConfirmUpTo(): number {
    return this.data?.settings?.party.autoConfirmUpTo ?? 99;
  }

  get needsApproval(): boolean {
    const party = Number(this.form?.get('partySize')?.value ?? 0);
    return this.data?.settings?.mode === 'request' || party > this.autoConfirmUpTo;
  }

  ngOnInit(): void {
    const r = this.data?.reservation;
    this.form = this.fb.group(
      {
        date: [r?.serviceDate ?? this.data?.date ?? this.today, Validators.required],
        partySize: [
          r?.partySize ?? 2,
          [Validators.required, Validators.min(1), Validators.max(this.maxStaffParty)],
        ],
        name: [
          r?.guest.name ?? '',
          [Validators.required, Validators.minLength(2), guestNameValidator()],
        ],
        // Each optional on its own, but one is needed (contactRequired below).
        // Validators.pattern passes an empty value. Same rules as Add staff.
        phone: [formatUsPhone(r?.guest.phone), Validators.pattern(US_PHONE_PATTERN)],
        email: [r?.guest.email ?? '', Validators.pattern(EMAIL_PATTERN)],
        occasion: [r?.occasion ?? ''],
        notes: [r?.notes ?? '', Validators.maxLength(300)],
        // The guest's standing notes (FNF-442), not this booking's: saved on
        // the guest and shown on their next bookings too.
        allergies: [r?.guest.standingNotes?.allergies ?? '', Validators.maxLength(200)],
        preferences: [r?.guest.standingNotes?.preferences ?? '', Validators.maxLength(300)],
        confirmNow: [true],
      },
      { validators: contactRequired },
    );
    this.selectedStartAt = r?.startAt ?? null;

    this.lookup
      .pipe(
        debounceTime(150),
        distinctUntilChanged(
          (a, b) => a.date === b.date && a.party === b.party && a.attempt === b.attempt,
        ),
        switchMap(({ date, party }) => {
          this.loadingSlots = true;
          // Staff may book any slot that has not started yet; the minimum
          // notice window applies to online guests only.
          this.slotsError = false;
          return forkJoin({
            slots: this.reservationService.availability(date, party),
            periods: this.reservationService.servicePeriods(date),
            hoursState: this.reservationService.servingHoursState(date),
            dayNote: this.reservationService.dayNote(date),
            timeZone: this.reservationService.timeZone(),
          }).pipe(
            // Keep the lookup stream alive so Retry can run it again.
            catchError(() => {
              this.slotsError = true;
              return of({
                slots: [],
                periods: [],
                hoursState: 'open' as ServingHoursState,
                dayNote: null as string | null,
                timeZone: this.timeZone,
              });
            }),
          );
        }),
      )
      .subscribe({
        next: ({ slots, periods, hoursState, dayNote, timeZone }) => {
          this.slots = slots;
          this.periods = periods;
          this.hoursState = hoursState;
          this.dayNote = dayNote;
          this.timeZone = timeZone;
          this.loadingSlots = false;
          // Keep the current selection only if it is still offered.
          const still = slots.find((s) => s.startAt === this.selectedStartAt);
          if (still && (still.available || this.isEdit)) {
            this.selectedSlot = still;
          } else if (!this.isEdit || !this.selectedStartAt) {
            this.selectedStartAt = null;
            this.selectedSlot = null;
          }
        },
      });

    this.form.get('date')!.valueChanges.subscribe(() => this.refreshSlots());
    this.form.get('partySize')!.valueChanges.subscribe(() => this.refreshSlots());
    this.refreshSlots();

    if (!this.isEdit) {
      // Recognise a returning guest as soon as the phone or email is complete.
      merge(this.form.get('phone')!.valueChanges, this.form.get('email')!.valueChanges)
        .pipe(
          debounceTime(400),
          map(() => this.lookupQuery()),
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
          switchMap((query) => (query ? this.reservationService.lookupGuest(query) : of(null))),
        )
        .subscribe((found) => this.recognise(found));
    }
  }

  /** A complete phone and/or a valid email, or null while neither is. */
  private lookupQuery(): { phone: string | null; email: string | null } | null {
    const phone = this.form.get('phone')!;
    const email = this.form.get('email')!;
    const digits = phone.valid ? usPhoneDigits(phone.value) : '';
    const query = {
      phone: digits.length === 10 ? digits : null,
      email: email.valid && email.value?.trim() ? String(email.value).trim() : null,
    };
    return query.phone || query.email ? query : null;
  }

  /**
   * Greet a returning guest: their allergies and preferences go into the
   * guest notes fields (unless the host already typed there), and one toast
   * says they have been here. A different, unknown guest clears what was
   * filled in for the previous one.
   */
  private recognise(found: GuestLookup | null): void {
    const allergies = this.form.get('allergies')!;
    const preferences = this.form.get('preferences')!;
    this.returning = found?.known ? found : null;
    this.firstTime = !!found && !found.known;
    if (this.returning) {
      const notes = this.returning.standingNotes;
      if (!allergies.dirty) allergies.setValue(notes.allergies ?? '');
      if (!preferences.dirty) preferences.setValue(notes.preferences ?? '');
      this.notesFromHistory = !!(notes.allergies || notes.preferences);
      this.greet(this.returning);
      return;
    }
    // Nobody we know: a different guest from now on, so they may be greeted.
    this.greeted = { existing: false, returning: false };
    if (this.notesFromHistory) {
      if (!allergies.dirty) allergies.setValue('');
      if (!preferences.dirty) preferences.setValue('');
      this.notesFromHistory = false;
    }
  }

  /**
   * One toast per guest, however they were found: the phone, the email, or
   * both one after the other give one lookup answer for the same guest, and
   * the "already has a reservation" toast says everything the "returning
   * guest" one would, so it stands in for it.
   */
  private greet(guest: GuestLookup): void {
    const allergy = guest.standingNotes.allergies
      ? ` Allergy: ${guest.standingNotes.allergies}.`
      : '';
    const [first, ...more] = guest.existing;
    if (first && !this.greeted.existing) {
      this.greeted = { existing: true, returning: true };
      const also = more.length ? ` (+${more.length} more)` : '';
      this.toastr.warning(
        `${first.guestName || 'This guest'} already has a reservation: ${this.existingLabel(
          first,
        )}${also}.${allergy}`,
        'Existing reservation',
        { timeOut: 10_000 },
      );
    } else if (!first && !this.greeted.returning) {
      this.greeted = { ...this.greeted, returning: true };
      this.toastr.info(`${returningGuestSummary(guest)} here.${allergy}`, 'Returning guest');
    }
  }

  /** "Thu 22 Oct, 7:00 PM · party of 2 · Patio 1 · Confirmed". */
  existingLabel(e: ExistingReservation): string {
    const day = formatInZone(new Date(e.startAt), this.timeZone, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
    const table = tableNamesLabel(e.tableNames);
    return [
      `${day}, ${timeLabel(new Date(e.startAt), this.timeZone)}`,
      `party of ${e.partySize}`,
      ...(table ? [table] : []),
      RESERVATION_STATUS_LABELS[e.status] ?? e.status,
    ].join(' · ');
  }

  /** "2 bookings · 3 visits · last visit 8 Oct". */
  get returningSummary(): string {
    const g = this.returning;
    if (!g) {
      return '';
    }
    const last = g.lastVisitAt
      ? ` · last visit ${formatInZone(new Date(g.lastVisitAt), this.timeZone, {
          day: 'numeric',
          month: 'short',
        })}`
      : '';
    return returningGuestSummary(g) + last;
  }

  refreshSlots(): void {
    const date = this.form.get('date')!.value;
    const party = Number(this.form.get('partySize')!.value);
    if (!date || !party || party < 1) {
      return;
    }
    this.lookup.next({ date, party, attempt: this.attempt });
  }

  /** Re-run the same lookup; the attempt count gets it past the unchanged-input filter. */
  retrySlots(): void {
    this.attempt++;
    this.refreshSlots();
  }

  /**
   * Why no time can be picked, or null when at least one slot is bookable.
   * Availability returns an empty list for "no hours set", "closed that day"
   * and "every table taken" alike, so the serving hours tell them apart.
   */
  get emptyReason(): SlotsEmptyReason | null {
    if (this.loadingSlots) {
      return null;
    }
    if (this.slotsError) {
      return 'error';
    }
    if (this.hoursState === 'unset') {
      return 'unset';
    }
    if (this.hoursState === 'closed') {
      return 'closed';
    }
    if (this.hoursState === 'closed_date') {
      return 'closed_date';
    }
    return this.slots.some((s) => s.available) ? null : 'full';
  }

  /** "Thu, Dec 25" for the selected date. */
  get dateLabel(): string {
    return dateLabel(String(this.form.get('date')!.value));
  }

  goToClosedDates(): void {
    this.dialogRef.close();
    this.router.navigate(['/main/reservation-settings']);
  }

  get weekdayName(): string {
    const [y, m, d] = String(this.form.get('date')!.value).split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long' });
  }

  goToServingHours(): void {
    this.dialogRef.close();
    this.router.navigate(['/main/restaurant-profile']);
  }

  /**
   * Which clock the times are on, shown only when the viewer's differs from
   * the restaurant's, e.g. "Times are in PDT, where it's 8:58 AM now."
   */
  get zoneNote(): string | null {
    const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!this.timeZone || this.timeZone === browserZone) {
      return null;
    }
    const now = new Date();
    const zoneName =
      new Intl.DateTimeFormat('en-US', { timeZone: this.timeZone, timeZoneName: 'short' })
        .formatToParts(now)
        .find((p) => p.type === 'timeZoneName')?.value ?? this.timeZone;
    return `Times are in ${zoneName}, where it's ${timeLabel(now, this.timeZone)} now.`;
  }

  /** Formats the phone as it is typed, like Add staff's Contact No. */
  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = formatUsPhone(input.value);
    input.value = formatted;
    this.form.get('phone')?.setValue(formatted, { emitEvent: false });
  }

  /** The table a slot would get, or why it can't be booked. */
  slotTooltip(slot: AvailabilitySlot): string {
    if (slot.available) {
      return slot.tableNames.length ? 'Table ' + tableNamesLabel(slot.tableNames) : 'Bookable';
    }
    return slot.reason || 'Unavailable';
  }

  slotsFor(period: ServicePeriod): AvailabilitySlot[] {
    const date = this.form.get('date')!.value as string;
    const start = atTime(date, period.start, this.timeZone).getTime();
    const end = periodEnd(date, period, this.timeZone).getTime();
    return this.slots.filter((s) => {
      const t = new Date(s.startAt).getTime();
      return t >= start && t < end;
    });
  }

  pick(slot: AvailabilitySlot): void {
    if (!slot.available && !(this.isEdit && slot.startAt === this.data.reservation?.startAt)) {
      return;
    }
    this.selectedStartAt = slot.startAt;
    this.selectedSlot = slot;
  }

  bumpParty(delta: number): void {
    const ctrl = this.form.get('partySize')!;
    const next = Math.min(Math.max(1, Number(ctrl.value) + delta), this.maxStaffParty);
    ctrl.setValue(next);
  }

  setOccasion(value: string): void {
    const ctrl = this.form.get('occasion')!;
    ctrl.setValue(ctrl.value === value ? '' : value);
  }

  /** Show "phone or email" once either field has been left, or Save was tried. */
  get showContactError(): boolean {
    return (
      this.form.hasError('contactRequired') &&
      (!!this.form.get('phone')?.touched || !!this.form.get('email')?.touched)
    );
  }

  get canSave(): boolean {
    return this.form.valid && !!this.selectedStartAt;
  }

  save(): void {
    if (!this.canSave) {
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.value;
    const result: CreateReservationInput = {
      startAt: this.selectedStartAt!,
      partySize: Number(v.partySize),
      guest: {
        name: String(v.name).trim(),
        phone: usPhoneDigits(v.phone) || null,
        email: v.email?.trim() || null,
        visits: this.data.reservation?.guest.visits ?? 0,
        userId: this.data.reservation?.guest.userId ?? null,
      },
      notes: v.notes?.trim() || null,
      occasion: v.occasion || null,
      channel: this.data.reservation?.channel ?? 'dashboard',
      status: this.needsApproval || !v.confirmNow ? 'pending' : 'confirmed',
      tableIds: this.selectedSlot?.tableIds ?? [],
      standingNotes: this.standingNotesChange(),
    };
    this.dialogRef.close(result);
  }

  /**
   * What the host changed in the guest's standing notes, or null. Occasions
   * and tags are edited on the guest card and left as they are here.
   */
  private standingNotesChange() {
    // A returning guest's notes are already saved: only edits go up.
    const before =
      this.data?.reservation?.guest.standingNotes ?? this.returning?.standingNotes ?? null;
    return standingNotesChange(before, {
      allergies: this.form.value.allergies ?? '',
      preferences: this.form.value.preferences ?? '',
      occasions: before?.occasions ?? '',
      tags: before?.tags ?? [],
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
